import RequirePalmettoFinanceUser from '@/components/auth/RequirePalmettoFinanceUser';
import RequirePermissions from '@/components/auth/requirePermissions';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import {
  useArchiveWarrantyPricingMutation,
  useCreateWarrantyPricingMutation,
  useGetWarrantiesQuery,
} from '@/services/pricing';
import { useGetClassificationsQuery } from '@/services/productCatalog';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  HVACEquipmentType,
  ProgramType,
  UserPermissions,
  warrantyMap,
  WarrantyPricingStatus,
  WarrantyType,
} from 'types';

import DateTimeDisplay from '../../DateTime';

import { AccessoryWarrantyModal } from './AccessoryWarrantyModal';
import { DuctlessMiniSplitWarrantyModal } from './DuctlessMiniSplitWarrantyModal';
import { EquipmentWarrantyModal } from './EquipmentWarrantyModal';
import { ZoningWarrantyModal } from './ZoningWarrantyModal';

export const Warranty = () => {
  const { isPhone } = useBreakpoint();
  const { programType = ProgramType.hvac, warrantyType = '' } = useParams<{
    programType: string;
    warrantyType: string;
  }>();
  const { data: classificationData = [] } = useGetClassificationsQuery({ path: 'hvac/equipment-materials' });
  const [createWarranty] = useCreateWarrantyPricingMutation();
  const [archiveWarranty] = useArchiveWarrantyPricingMutation();
  const {
    data: warrantyData = [],
    isLoading,
    isFetching,
  } = useGetWarrantiesQuery({ type: warrantyType, programType }, { skip: !warrantyType || !programType });
  const equipment = useMemo(() => {
    const parentClassificationIndex = classificationData.findIndex(
      (classification) => classification.slug === 'equipment-materials',
    );
    const ductlessIndoorIndex = classificationData.findIndex(
      (classification) => classification.slug === 'ductless-indoor',
    );
    const ductlessOutdoorIndex = classificationData.findIndex(
      (classification) => classification.slug === 'ductless-outdoor',
    );
    const classifications = [...classificationData];
    classifications.splice(parentClassificationIndex, 1);
    classifications.splice(ductlessIndoorIndex, 1);
    classifications.splice(ductlessOutdoorIndex, 1);
    return classifications.map((classification) => ({
      name: classification.name,
      type:
        classification.defaultAttributes?.find((attribute) => attribute.key === 'lightreach-product-type')?.value ||
        'other',
    }));
  }, [classificationData]);

  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();

  const archiveWarrantyPricing = useCallback(
    async (id: string) => {
      const confirm = window.confirm('Are you sure you want to archive this warranty pricing?');
      if (!confirm) {
        return;
      }
      if (archiveWarranty) {
        try {
          await archiveWarranty({ id }).unwrap();
          toast.success('Warranty pricing archived');
        } catch (e) {
          console.error(e);
          toast.error('Warranty failed to archive');
        }
      }
    },
    [archiveWarranty],
  );

  const columnConfig = useMemo(() => {
    const baseConfig = [
      {
        heading: 'Status',
        dataKey: 'status',
        width: 125,
        render: (_cell: any, row: any) => (
          <Badge
            variant={row.status === 'active' ? 'success' : row.status === 'archived' ? 'warning' : 'danger'}
            message={row.status}
          />
        ),
      },
      { heading: 'Provider', dataKey: 'warrantyProvider' },
      { heading: 'Payment Frequency', dataKey: 'paymentFrequency' },
    ];
    const dateConfig = [
      {
        heading: 'Created',
        dataKey: 'createdAt',
        render: (cell: any) => <DateTimeDisplay value={cell} dateFormat={'DD'} excludeTime />,
      },
      {
        heading: 'Archived',
        dataKey: 'archivedAt',
        render: (cell: any) => <DateTimeDisplay value={cell} dateFormat={'DD'} excludeTime />,
      },
      {
        heading: '',
        dataKey: 'id',
        render: (cell: any, row: any) => (
          <Button
            onClick={() => archiveWarrantyPricing(cell)}
            iconPrefix="archive"
            size="sm"
            variant="tertiary"
            tone="neutral"
            disabled={row.status !== WarrantyPricingStatus.active}
          />
        ),
      },
    ];

    switch (warrantyType) {
      case WarrantyType.equipment:
        return [
          ...baseConfig,
          ...(equipment ?? []).map((item) => ({
            heading: item.name,
            render: (_cell: any, row: any) =>
              row.equipment && row.equipment[item.type as HVACEquipmentType] !== undefined
                ? currencyFormatter.format(row.equipment[item.type as HVACEquipmentType])
                : 'N/A',
          })),
          ...dateConfig,
        ];
      case WarrantyType.accessories:
        return [
          ...baseConfig,
          {
            heading: 'Min Cost',
            dataKey: 'accessories',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{currencyFormatter.format(item.minCost)}</Box>
                ))}
              </Box>
            ),
          },
          {
            heading: 'Max Cost',
            dataKey: 'accessories',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{currencyFormatter.format(item.maxCost)}</Box>
                ))}
              </Box>
            ),
          },
          {
            heading: 'Warranty Cost',
            dataKey: 'accessories',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{currencyFormatter.format(item.warrantyCost)}</Box>
                ))}
              </Box>
            ),
          },
          ...dateConfig,
        ];
      case WarrantyType.zoning:
        return [
          ...baseConfig,
          {
            heading: 'Min Zones',
            dataKey: 'zoning',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{item.minZones}</Box>
                ))}
              </Box>
            ),
          },
          {
            heading: 'Max Zones',
            dataKey: 'zoning',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{item.maxZones}</Box>
                ))}
              </Box>
            ),
          },
          {
            heading: 'Warranty Cost',
            dataKey: 'zoning',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{currencyFormatter.format(item.warrantyCost)}</Box>
                ))}
              </Box>
            ),
          },
          {
            heading: 'Per Zone',
            dataKey: 'zoning',
            render: (cell: any) => (
              <Box direction="column" gap="xs">
                {cell?.map((item: any) => (
                  <Box key={item.id}>{item.perZone ? 'True' : 'False'}</Box>
                ))}
              </Box>
            ),
          },
          ...dateConfig,
        ];
      case WarrantyType.ductlessMiniSplit:
        return [
          ...baseConfig,
          {
            heading: '1 Indoor & 1 Outdoor Cost',
            dataKey: 'ductlessMiniSplit',
            render: (cell: any) => currencyFormatter.format(cell?.indoorOutdoorCost),
          },
          {
            heading: 'Additional Indoor Cost',
            dataKey: 'ductlessMiniSplit',
            render: (cell: any) => currencyFormatter.format(cell?.additionalIndoorCost),
          },
          ...dateConfig,
        ];
      default:
        return [...baseConfig, ...dateConfig];
    }
  }, [warrantyType, equipment, archiveWarrantyPricing]);

  const handleSubmit = async (data: any) => {
    try {
      await createWarranty({ data, programType: programType as ProgramType }).unwrap();
      closeModal();
    } catch (e) {
      console.error(e);
    }
  };
  const renderWarrantyModal = () => {
    switch (warrantyType) {
      case WarrantyType.equipment:
        return (
          <EquipmentWarrantyModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            warrantyType={warrantyType}
            onSubmit={handleSubmit}
          />
        );
      case WarrantyType.accessories:
        return (
          <AccessoryWarrantyModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            warrantyType={warrantyType}
            onSubmit={handleSubmit}
          />
        );
      case WarrantyType.zoning:
        return (
          <ZoningWarrantyModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            warrantyType={warrantyType}
            onSubmit={handleSubmit}
          />
        );
      case WarrantyType.ductlessMiniSplit:
        return (
          <DuctlessMiniSplitWarrantyModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            warrantyType={warrantyType}
            onSubmit={handleSubmit}
          />
        );
    }
  };
  return (
    <>
      {renderWarrantyModal()}
      <Card padding="sm">
        <Card.Header>
          <Box direction="row" justifyContent="space-between">
            <Box fontSize="md" fontWeight="medium">
              {warrantyMap[programType as ProgramType][warrantyType as WarrantyType]}
            </Box>
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
                <Button
                  as="button"
                  variant="primary"
                  iconPrefix="add"
                  size={{ base: 'sm', desktop: 'md' }}
                  aria-label="create warranty"
                  onClick={openModal}
                >
                  {!isPhone && 'Option'}
                </Button>
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </Box>
        </Card.Header>
        <Table
          rowKey="id"
          columns={columnConfig as Column[]}
          rows={warrantyData}
          isLoading={isLoading || isFetching}
          isScrollable={{ x: true }}
        />
      </Card>
    </>
  );
};
